import {
  Auth,
  AuthProvider,
  signInWithPopup,
} from "firebase/auth";

import firebase, { auth } from "../config/fbConfig";
import { PostHog } from "posthog-js/react";
import { httpsCallable } from "firebase/functions";
import { functions } from "../config/fbConfig";
import { toast } from "react-toastify";
import { isRegistrationAllowedForEmail, REGISTRATION_ENABLED, SIGN_IN_BLOCKED_MESSAGE } from "../consts";

// Crisp removed

export function handleGoogleSignUp(auth: Auth, provider: AuthProvider, navigate: (arg0: string) => void, httpsCallable: (arg0: any, arg1: string) => any, functions: any, newsletter_accepted: Boolean, posthog: PostHog, reqData: String | null, discount: string | '', distinctId: string): Promise<void> {
  return new Promise((resolve, reject) => {
    signInWithPopup(auth, provider).then(async (result: any) => {
      // The signed-in user info.
      const user = result.user;
      const { email, uid } = user;
      const full_name = user.displayName;
      // check if the user already exists in the database
      const userRef = firebase.firestore().collection("users").doc(uid);

      // Identify user with posthog
      if (posthog) {
        posthog.identify(uid);
        posthog.people.set({
          $email: email,
          $name: full_name,
          $created: user.createdAt,
          $last_login: user.lastLoginAt,
          $user_id: uid,
        });
      }

      const doc = await userRef.get();
      if (doc.exists) {
        // While registration is closed, only accounts active within the last
        // 6 months may still sign in. The popup has already signed the user in
        // at this point, so the backend decides from its own last_sign_in_at
        // stamp and we sign the session out again if the account went
        // inactive. Fails open if the check itself errors.
        if (!REGISTRATION_ENABLED) {
          try {
            const checkSignInAllowed = httpsCallable(functions, "user_authentication_handler");
            const res: any = await checkSignInAllowed({ action: "check_sign_in_allowed", email });
            if (res?.data?.allowed === false) {
              if (posthog) {
                posthog.capture('sign_in:blocked_inactive_account', {
                  provider: 'google',
                });
              }
              await auth.signOut();
              toast.error(SIGN_IN_BLOCKED_MESSAGE, {
                position: "top-right",
                autoClose: 7000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
                progress: undefined,
                theme: "light",
              });
              reject(new Error("sign-in-blocked-inactive"));
              return;
            }
          } catch (error) {
            console.error("Sign-in activity check failed:", error);
          }
        }

        // Capture sign in event with posthog
        if (posthog) {
          try {
            posthog.capture('sign_in:google_popup', {
              $set: {
                last_sign_in: new Date(Number(user.metadata.lastLoginAt)).toISOString(),
              }
            });
          } catch (error) {
            console.error("Posthog error:", error);
          }
        }
        // user already exists, redirect to templates page
        handleUserRedirect(auth as any, navigate, posthog, false, discount);
      } else if (!isRegistrationAllowedForEmail(email)) {
        // Registration is disabled for this domain: the Google account has no
        // Robowriter account, so sign the popup session out again instead of
        // creating one.
        if (posthog) {
          posthog.capture('sign_up:blocked_registration_disabled', {
            provider: 'google',
          });
        }
        await auth.signOut();
        toast.error(
          "Die Registrierung neuer Konten ist derzeit deaktiviert. Bitte melde Dich mit einem bestehenden Konto an.",
          {
            position: "top-right",
            autoClose: 7000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            progress: undefined,
            theme: "light",
          }
        );
        reject(new Error("registration-disabled"));
        return;
      } else {
        // user does not exist, create user document, send welcome mail, and redirect to /welcome
        // call createUserDocument function from firebase functions

        // Capture sign up event with posthog
        if (posthog) {
          posthog.capture('sign_up:google_popup', {
            $set: {
              last_sign_in: new Date(Number(user.metadata.lastLoginAt)).toISOString(),
            }
          });
        }

        const createUserDocumentFunction = httpsCallable(
          functions,
          "user_authentication_handler"
        );

        if (reqData == null) {
          reqData = "";
        }

        // Update the function call to include the action parameter
        await createUserDocumentFunction({ 
          action: "create_user",
          full_name, 
          email, 
          uid, 
          newsletter_subscribed: newsletter_accepted, 
          google_sign_up: true, 
          reqData: reqData,
          posthog_distinct_id: distinctId === "" ? posthog.get_distinct_id() : distinctId
        });

        // redirect to welcome page, set sign_up flag to true
        handleUserRedirect(auth as any, navigate, posthog, true);
      }
      resolve();
    }).catch((error) => {
      if (error.code === 'auth/popup-closed-by-user') {
        console.log('Sign-in popup was closed by the user.');
        // You can add any specific handling for this case here
        // For example, you might want to show a message to the user
        if (posthog) {
          posthog.capture('sign_in:google_popup_closed', {
            error: error.message
          });
        }
      } else {
        // Handle other errors
        console.error("Error during Google sign-in:", error);
        if (posthog) {
          posthog.capture('sign_in:google_error', {
            error: error.message
          });
        }
      }
      reject(error);
    });
  });
};

export async function userDocExists(uid: string) {
  const doc = await firebase
    .firestore()
    .collection("users")
    .doc(uid)
    .get();
  if (doc.exists) {
    return true;
  } else {
    return false;
  }
};

// If the user is logged in and verified, redirect them to the templates page
// If a user is logged in and not verified, redirect them to the verify email page
export async function checkUserState(auth: Auth, navigate: (arg0: string) => void, posthog:PostHog) {
  if (auth.currentUser?.uid && auth.currentUser.emailVerified) {
    // Check if the user doc exists, if it does, redirect them to the templates page
    // If it doesn't we don't want to redirect them here, since if they used another provider such as google/apple
    // The user will be logged in and verified before their user doc is created.
    // Thus we want to wait until the user doc is created and rely on the redirect in the utils/loginUtils.ts function
    if (await userDocExists(auth.currentUser.uid)) {
      handleUserRedirect(auth, navigate, posthog);
    }
  } else if (auth.currentUser?.uid && !auth.currentUser.emailVerified) {
    navigate("/verify-email");
  }
};

export async function handleUserRedirect(auth: Auth, navigate: (arg0: string) => void, posthog: PostHog, sign_up: boolean = false, discount: string = '') {
  const userDoc = await firebase.firestore().collection("users").doc(auth.currentUser?.uid).get();

  let navigateUrl = "/home";
  let welcomeUrl = "/welcome";
  let upgradeUrl = "/upgrade";

  // Get the current URL parameters
  const currentUrlParams = new URLSearchParams(window.location.search).toString();
  const paramString = currentUrlParams ? `?${currentUrlParams}` : '';

  // Get the firebase function to updateUserData 
  const updateUserDataFunction = httpsCallable(functions, "update_user_data_py");

  // If discount is not empty, redirect to upgrade page with discount
  if (discount !== '') {
    navigate(`${upgradeUrl}${paramString}`);
    return;
  }

  // Handle Google sign up users, to not cause a race condition with the user doc creation
  navigate(`${welcomeUrl}${paramString}`);
  if (sign_up && auth.currentUser?.providerData[0]?.providerId === "google.com") {
    handleSignUpTracking(posthog);
    navigate(`${navigateUrl}${paramString}`);
    window.location.reload();
  } else if (userDoc.data()?.welcome_popup_seen) {
    navigate(`${navigateUrl}${paramString}`);
  } else if (userDoc.data()?.welcome_popup_seen === false) {
    // Update the user document and set the flag "welcome_popup_seen" to true.
    // A failing backend call must not strand the user on the current page --
    // the reload below also refreshes the auth token after email verification,
    // so the redirect has to happen even if this update fails (the popup would
    // simply show once more on the next visit).
    try {
      await updateUserDataFunction({
        welcome_popup_seen: true,
      });
    } catch (error) {
      console.error("Failed to update welcome_popup_seen:", error);
    }
    handleSignUpTracking(posthog);
    navigate(`${welcomeUrl}${paramString}`);
    window.location.reload();
  } else {
    // If the user doc flag doesn't exist, redirect to templates
    navigate(`${navigateUrl}${paramString}`);
  }
};

/* Logout Function */
export async function logoutUser(auth: Auth, navigate: (arg0: string) => void) {
  // Crisp session reset removed
  
  // Preserve URL parameters when redirecting to sign-in
  const currentUrlParams = new URLSearchParams(window.location.search).toString();
  const paramString = currentUrlParams ? `?${currentUrlParams}` : '';
  
  auth.signOut().then(() => {
    navigate(`/sign-in${paramString}`);
  });
}

const handleSignUpTracking = (posthog: PostHog) => {
  // Check if the tracking has already been done
  const hasRun = sessionStorage.getItem("leadTracking");

  // If the page is not fully loaded, wait for it to load
  if (document.readyState !== 'complete') {
      window.addEventListener('load', () => {
          handleSignUpTracking(posthog);
      });
      return;
  }

  if (!hasRun) { // Test if cookie lowers our true conversion.

      // Facebook Pixel Lead event tracking
      if (typeof window !== 'undefined' && (window as any).fbq) {
          (window as any).fbq('track', 'Lead');
      }

      // Google Enhanced Conversion Tracking Code
      // Needs to fire before the actual event code, otherwise the attribute is not set
      // ref: https://support.google.com/google-ads/answer/13262500
      if (typeof window !== 'undefined' && (window as any).dataLayer) {
          (window as any).dataLayer.push({
              'email_address': auth.currentUser?.email,
          });
      }

      // Google Tag Manager code for conversion tracking
      if (typeof window !== 'undefined' && (window as any).dataLayer) {
          (window as any).dataLayer.push({
              'event': 'SignUp',
          });
      }

      // Bing UET tag for conversion tracking
      if (typeof window !== 'undefined' && (window as any).uetq) {
          (window as any).uetq.push(
              'event', 'SignUp', {
          });
      }

      // Posthog event sign_up:completed
      let allCookiesAccepted = false;                    
      // Add this code to print the cookie
      const iubCookie = document.cookie
          .split('; ')
          .find(row => row.startsWith('_iub_cs-91682523='));
      if (iubCookie) {
          // Extract the value part after the '='
          const cookieValue = iubCookie.split('=')[1];
          // Decode the URL-encoded string and parse the JSON
          const decodedCookie = JSON.parse(decodeURIComponent(cookieValue));
          
          // Check if purpose exists and all its keys are true
          if (decodedCookie.purposes) {
              const allPurposesAccepted = Object.values(decodedCookie.purposes).every(value => value === true);
              if (allPurposesAccepted) {
                  allCookiesAccepted = true
              }
          }
      }

      posthog.capture('sign_up:handleSignUpTracking', {
          cookies_accepted: allCookiesAccepted
      });
      // Set a cookie to prevent tracking from firing multiple times
      sessionStorage.setItem("leadTracking", "true");
  }
};